import {ANTIBODY_SCHEMA_INTERFACE_ID} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TagSchemaSupportedFolderItemType} from 'constants/RegistryConstants';
import {TagSchemaItemType, TagSchemaSequenceSchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {EQUIPMENT_SCHEMA_INTERFACE_ID} from 'instruments/equipment/EquipmentConstants';
import {ComplexPolymerInterfaceApiId} from 'modbio/complex-polymers/ComplexPolymerSchemaInterfaceConstants';
import {assertNever} from 'util/Types';

export function getItemTypeDisplayName(itemType: TagSchemaItemType): string {
  switch (itemType) {
    case TagSchemaItemType.AA_SEQUENCE:
      return 'AA Sequence';
    case TagSchemaItemType.ANTIBODY:
      return 'Antibody';
    case TagSchemaItemType.BASIC_FOLDER_ITEM:
      return 'Custom Entity';
    case TagSchemaItemType.DNA_SEQUENCE:
      return 'DNA Sequence';
    case TagSchemaItemType.DNA_OLIGO:
      return 'DNA Oligo';
    case TagSchemaItemType.ENTRY:
      return 'Entry';
    case TagSchemaItemType.EQUIPMENT:
      return 'Equipment';
    case TagSchemaItemType.MIXED:
      return 'Sequence (DNA or Oligo)';
    case TagSchemaItemType.MIXTURE:
      return 'Mixture';
    case TagSchemaItemType.MOLECULE:
      return 'Molecule';
    case TagSchemaItemType.OLIGO_CONJUGATE:
      return 'Oligo Conjugate';
    case TagSchemaItemType.OLIGO_DUPLEX:
      return 'Oligo Duplex';
    case TagSchemaItemType.RNA_OLIGO:
      return 'RNA Oligo';
    case TagSchemaItemType.RNA_SEQUENCE:
      return 'RNA Sequence';
    default:
      assertNever(itemType);
      return '(unknown)';
  }
}

// Temporary function to convert from new overall item type back to folderItemType and sequenceType
export function itemTypeToFolderItemTypeAndSequenceType(itemType: TagSchemaItemType): {
  folderItemType: TagSchemaSupportedFolderItemType;
  sequenceType: TagSchemaSequenceSchemaType | null;
} {
  switch (itemType) {
    case TagSchemaItemType.AA_SEQUENCE:
      return {folderItemType: ModelTypeConstants.PROTEIN, sequenceType: null};
    case TagSchemaItemType.ANTIBODY:
      return {folderItemType: ModelTypeConstants.BASIC_FOLDER_ITEM, sequenceType: null};
    case TagSchemaItemType.BASIC_FOLDER_ITEM:
      return {folderItemType: ModelTypeConstants.BASIC_FOLDER_ITEM, sequenceType: null};
    case TagSchemaItemType.DNA_OLIGO:
      return {folderItemType: ModelTypeConstants.SEQUENCE, sequenceType: TagSchemaSequenceSchemaType.OLIGO};
    case TagSchemaItemType.DNA_SEQUENCE:
      return {
        folderItemType: ModelTypeConstants.SEQUENCE,
        sequenceType: TagSchemaSequenceSchemaType.DNA_SEQUENCE,
      };
    case TagSchemaItemType.ENTRY:
      return {folderItemType: ModelTypeConstants.ENTRY, sequenceType: null};
    case TagSchemaItemType.EQUIPMENT:
      return {folderItemType: ModelTypeConstants.BASIC_FOLDER_ITEM, sequenceType: null};
    case TagSchemaItemType.MIXED:
      return {folderItemType: ModelTypeConstants.SEQUENCE, sequenceType: TagSchemaSequenceSchemaType.MIXED};
    case TagSchemaItemType.MIXTURE:
      return {folderItemType: ModelTypeConstants.MIXTURE, sequenceType: null};
    case TagSchemaItemType.MOLECULE:
      return {folderItemType: ModelTypeConstants.MOLECULE, sequenceType: null};
    case TagSchemaItemType.OLIGO_CONJUGATE:
      return {folderItemType: ModelTypeConstants.BASIC_FOLDER_ITEM, sequenceType: null};
    case TagSchemaItemType.OLIGO_DUPLEX:
      return {folderItemType: ModelTypeConstants.BASIC_FOLDER_ITEM, sequenceType: null};
    case TagSchemaItemType.RNA_OLIGO:
      return {
        folderItemType: ModelTypeConstants.SEQUENCE,
        sequenceType: TagSchemaSequenceSchemaType.RNA_OLIGO,
      };
    case TagSchemaItemType.RNA_SEQUENCE:
      return {
        folderItemType: ModelTypeConstants.SEQUENCE,
        sequenceType: TagSchemaSequenceSchemaType.RNA_SEQUENCE,
      };
    default:
      assertNever(itemType);
      return {folderItemType: ModelTypeConstants.BASIC_FOLDER_ITEM, sequenceType: null};
  }
}

// Temporary convenience function to convert the deprecated folderItemType and sequenceType to itemType
export function folderItemTypeAndSequenceTypeToItemType(
  folderItemType: TagSchemaSupportedFolderItemType,
  sequenceType: TagSchemaSequenceSchemaType | null
): TagSchemaItemType {
  switch (folderItemType) {
    case ModelTypeConstants.PROTEIN:
      return TagSchemaItemType.AA_SEQUENCE;
    case ModelTypeConstants.BASIC_FOLDER_ITEM:
      return TagSchemaItemType.BASIC_FOLDER_ITEM;
    case ModelTypeConstants.ENTRY:
      return TagSchemaItemType.ENTRY;
    case ModelTypeConstants.MIXTURE:
      return TagSchemaItemType.MIXTURE;
    case ModelTypeConstants.MOLECULE:
      return TagSchemaItemType.MOLECULE;
    case ModelTypeConstants.SEQUENCE:
      if (sequenceType === TagSchemaSequenceSchemaType.DNA_SEQUENCE) {
        return TagSchemaItemType.DNA_SEQUENCE;
      } else if (sequenceType === TagSchemaSequenceSchemaType.OLIGO) {
        return TagSchemaItemType.DNA_OLIGO;
      } else if (sequenceType === TagSchemaSequenceSchemaType.RNA_SEQUENCE) {
        return TagSchemaItemType.RNA_SEQUENCE;
      } else if (sequenceType === TagSchemaSequenceSchemaType.RNA_OLIGO) {
        return TagSchemaItemType.RNA_OLIGO;
      } else if (sequenceType === TagSchemaSequenceSchemaType.MIXED) {
        return TagSchemaItemType.MIXED;
      } else {
        return TagSchemaItemType.DNA_SEQUENCE;
      }
    default:
      return TagSchemaItemType.BASIC_FOLDER_ITEM;
  }
}

export function getStaticSchemaInterfaceIdFromItemType(itemType: TagSchemaItemType): string | null {
  switch (itemType) {
    case TagSchemaItemType.ANTIBODY:
      return ANTIBODY_SCHEMA_INTERFACE_ID;
    case TagSchemaItemType.OLIGO_CONJUGATE:
      return ComplexPolymerInterfaceApiId.OLIGO_CONJUGATE_ID;
    case TagSchemaItemType.OLIGO_DUPLEX:
      return ComplexPolymerInterfaceApiId.OLIGO_DUPLEX_ID;
    case TagSchemaItemType.EQUIPMENT:
      return EQUIPMENT_SCHEMA_INTERFACE_ID;
    default:
      return null;
  }
}

export function showStaticSchemaInterfaceAsType(interfaceId: string): boolean {
  const exclusiveSSIIds = [
    ANTIBODY_SCHEMA_INTERFACE_ID,
    EQUIPMENT_SCHEMA_INTERFACE_ID,
    ComplexPolymerInterfaceApiId.OLIGO_CONJUGATE_ID,
    ComplexPolymerInterfaceApiId.OLIGO_DUPLEX_ID,
  ];
  return exclusiveSSIIds.includes(interfaceId);
}
